import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface IToolbarMenuProps {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement>;
    width?: number;
    keepMounted?: boolean;
    className?: string;
    children: React.ReactNode;
}

const VIEWPORT_GUTTER = 8;

// Тулбар горизонтально скроллится (overflow), абсолютное меню внутри него обрезалось бы, поэтому меню в портале.
// Правый край меню выравнивается по правому краю кнопки: меню раскрывается влево и не уходит за экран.
const ToolbarMenu: React.FC<IToolbarMenuProps> = ({ open, onClose, anchorRef, width, keepMounted, className = '', children }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: VIEWPORT_GUTTER });

    useLayoutEffect(() => {
        if (!open || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        let right = Math.max(VIEWPORT_GUTTER, window.innerWidth - rect.right);
        if (width) right = Math.min(right, window.innerWidth - width - VIEWPORT_GUTTER);
        setPos({ top: rect.bottom + 4, right });
    }, [open, anchorRef, width]);

    useEffect(() => {
        if (!open) return;
        const onOutside = (e: Event) => {
            const target = e.target as Node;
            if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
            onClose();
        };
        const onResize = () => onClose();
        const onScroll = (e: Event) => {
            const scrolled = e.target;
            const anchor = anchorRef.current;
            if (scrolled === document || (scrolled instanceof Node && anchor && scrolled.contains(anchor))) onClose();
        };
        document.addEventListener('mousedown', onOutside);
        document.addEventListener('touchstart', onOutside);
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('touchstart', onOutside);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open, onClose, anchorRef]);

    if (!open && !keepMounted) return null;

    return ReactDOM.createPortal(
        <div
            ref={menuRef}
            style={{
                position: 'fixed',
                top: pos.top,
                right: pos.right,
                width: width ?? 'max-content',
                maxWidth: `calc(100vw - ${VIEWPORT_GUTTER * 2}px)`,
                zIndex: 1000,
                display: open ? 'block' : 'none',
            }}
            className={className}
        >
            {children}
        </div>,
        document.body,
    );
};

export default ToolbarMenu;
